import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { LoadingSpinner } from '../../components/general/LoadingSpinner';
import ViewToggle from '../../components/data_hub/ViewToggle';
import DataTable from '../../components/data_hub/DataTable';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';
import { FaArrowLeft, FaSearch, FaTimes, FaDollarSign } from 'react-icons/fa';

const TerritoryMapView = () => {
  const navigate = useNavigate();
  const authToken = localStorage.getItem('authToken');
  const { isLoaded, loadError } = useGoogleMaps();
  
  const [view, setView] = useState('map');
  const [territories, setTerritories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Map state
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const polygonsRef = useRef([]);
  const [selectedTerritory, setSelectedTerritory] = useState(null);
  const [utilityRates, setUtilityRates] = useState([]);
  const [loadingRates, setLoadingRates] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedRegulated, setSelectedRegulated] = useState('');
  
  const [availableStates, setAvailableStates] = useState([]);

  const fetchUtilityRates = useCallback(async (territoryId) => {
    setLoadingRates(true);
    try {
      const response = await axios.get(API_ENDPOINTS.getUtilityRatesByTerritory, {
        params: { territory_id: territoryId },
        headers: { Authorization: `Token ${authToken}` },
      });
      setUtilityRates(response.data.rates || []);
    } catch (err) {
      console.error('Error fetching utility rates:', err);
      setUtilityRates([]);
    } finally {
      setLoadingRates(false);
    }
  }, [authToken]);

  const fetchTerritories = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(API_ENDPOINTS.getUtilityTerritories, {
        headers: { Authorization: `Token ${authToken}` },
        params: { geometry: 'true', page_size: 500 },
      });

      const territoriesData = response.data.territories?.features || [];
      const parsedTerritories = territoriesData.map(feature => ({
        ...feature.properties,
        id: feature.id,
        geometry: feature.geometry,
      }));

      setTerritories(parsedTerritories);
      const states = [...new Set(parsedTerritories.map(t => t.STATE).filter(Boolean))].sort();
      setAvailableStates(states);
    } catch (err) {
      console.error('Error fetching territories:', err);
      setError('Failed to load utility territories. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  const getFilteredTerritories = useCallback(() => {
    return territories.filter(territory => {
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          territory.NAME?.toLowerCase().includes(searchLower) ||
          territory.STATE?.toLowerCase().includes(searchLower) ||
          territory.CITY?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }
      if (selectedState && territory.STATE !== selectedState) return false;
      if (selectedType && !territory.TYPE?.includes(selectedType)) return false;
      if (selectedRegulated && territory.REGULATED !== selectedRegulated) return false;
      return true;
    });
  }, [territories, searchTerm, selectedState, selectedType, selectedRegulated]);

  const handleTerritoryClick = useCallback((territory, polygon) => {
    setSelectedTerritory(territory);
    polygonsRef.current.forEach(p => p.setOptions({ fillOpacity: 0.2, strokeWeight: 1.5 }));
    polygon.setOptions({ fillOpacity: 0.5, strokeWeight: 3 });
    if (territory.id) {
      fetchUtilityRates(territory.id);
    }
  }, [fetchUtilityRates]);

  const renderPolygons = useCallback((map) => {
    polygonsRef.current.forEach(polygon => polygon.setMap(null));
    polygonsRef.current = [];

    const filteredTerritories = getFilteredTerritories();

    filteredTerritories.forEach((territory) => {
      if (!territory.geometry) return;

      let coordinates = [];
      if (territory.geometry.type === 'MultiPolygon') {
        territory.geometry.coordinates.forEach(polygonCoords => {
          polygonCoords.forEach(ring => {
            coordinates.push(ring.map(coord => ({ lat: coord[1], lng: coord[0] })));
          });
        });
      } else if (territory.geometry.type === 'Polygon') {
        territory.geometry.coordinates.forEach(ring => {
          coordinates.push(ring.map(coord => ({ lat: coord[1], lng: coord[0] })));
        });
      }

      coordinates.forEach(paths => {
        const polygon = new window.google.maps.Polygon({
          paths,
          strokeColor: '#2563EB',
          strokeOpacity: 0.8,
          strokeWeight: 1.5,
          fillColor: '#3B82F6',
          fillOpacity: 0.2,
          map,
        });

        polygon.addListener('click', () => handleTerritoryClick(territory, polygon));
        polygon.addListener('mouseover', () => polygon.setOptions({ fillOpacity: 0.4, strokeWeight: 2.5 }));
        polygon.addListener('mouseout', () => {
          if (selectedTerritory?.id !== territory.id) {
            polygon.setOptions({ fillOpacity: 0.2, strokeWeight: 1.5 });
          }
        });

        polygonsRef.current.push(polygon);
      });
    });
  }, [getFilteredTerritories, handleTerritoryClick, selectedTerritory]);

  const initializeMap = useCallback(() => {
    if (!window.google || !mapRef.current) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: 39.8283, lng: -98.5795 },
      zoom: 4,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
    });

    mapInstanceRef.current = map;
    renderPolygons(map);
  }, [renderPolygons]);

  const handleReset = () => {
    setSearchTerm('');
    setSelectedState('');
    setSelectedType('');
    setSelectedRegulated('');
  };

  useEffect(() => {
    if (isLoaded) {
      fetchTerritories();
    }
  }, [isLoaded, fetchTerritories]);

  useEffect(() => {
    if (isLoaded && view === 'map' && mapRef.current && !mapInstanceRef.current) {
      initializeMap();
    }
  }, [isLoaded, view, initializeMap]);

  useEffect(() => {
    if (mapInstanceRef.current) {
      renderPolygons(mapInstanceRef.current);
    }
  }, [territories, searchTerm, selectedState, selectedType, selectedRegulated, renderPolygons]);

  const tableColumns = [
    { key: 'NAME', label: 'Utility Name', format: 'string' },
    { key: 'STATE', label: 'State', format: 'string' },
    { key: 'TYPE', label: 'Type', format: 'string' },
    { key: 'CUSTOMERS', label: 'Customers', format: 'integer' },
    { key: 'REGULATED', label: 'Regulated', format: 'string' },
  ];

  return (
    <div className="h-screen flex flex-col bg-gray-50">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/data')}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FaArrowLeft />
                  <span>Back to DataHub</span>
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 font-ui">Utility Service Territories</h1>
                  <p className="text-sm text-gray-600">Geographic boundaries of electric utilities</p>
                </div>
              </div>
              <ViewToggle view={view} onViewChange={setView} />
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
                <FaSearch className="absolute left-3 top-3 text-gray-400" />
              </div>

              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All States</option>
                {availableStates.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>

              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="Investor Owned">Investor Owned</option>
                <option value="Municipal">Municipal</option>
                <option value="Cooperative">Cooperative</option>
              </select>

              <select
                value={selectedRegulated}
                onChange={(e) => setSelectedRegulated(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Regulated Status</option>
                <option value="Regulated">Regulated</option>
                <option value="Unregulated">Unregulated</option>
              </select>

              <button
                onClick={handleReset}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Content */}
          {view === 'map' ? (
            <div className="flex-1 relative">
              <div ref={mapRef} className="w-full h-full" />

              {/* Google Maps script error */}
              {loadError && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center max-w-md">
                    <p className="text-red-700 font-medium">
                      Failed to load Google Maps: {loadError.message}
                    </p>
                  </div>
                </div>
              )}

              {/* Data loading overlay (keep basemap visible) */}
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                  <LoadingSpinner message="Loading territories..." />
                </div>
              )}

              {/* Data error overlay (keep basemap visible) */}
              {error && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center max-w-md">
                    <p className="text-red-600 font-medium">{error}</p>
                  </div>
                </div>
              )}

              {/* Territory Info Sidebar */}
              {selectedTerritory && (
                <div className="absolute top-4 right-4 bg-white rounded-lg shadow-xl p-6 max-w-md max-h-[80vh] overflow-y-auto">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{selectedTerritory.NAME}</h3>
                      <p className="text-gray-600">{selectedTerritory.STATE}</p>
                    </div>
                    <button
                      onClick={() => setSelectedTerritory(null)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <FaTimes />
                    </button>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div>
                      <label className="text-xs font-medium text-gray-500">Type</label>
                      <p className="text-sm text-gray-900">{selectedTerritory.TYPE || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Customers</label>
                      <p className="text-sm text-gray-900">{selectedTerritory.CUSTOMERS?.toLocaleString() || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Regulated</label>
                      <p className="text-sm text-gray-900">{selectedTerritory.REGULATED || 'N/A'}</p>
                    </div>
                  </div>

                  {/* Utility Rates Section */}
                  <div className="border-t pt-4">
                    <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <FaDollarSign className="text-green-600" />
                      Utility Rates
                    </h4>
                    {loadingRates ? (
                      <p className="text-sm text-gray-600">Loading rates...</p>
                    ) : utilityRates.length > 0 ? (
                      <div className="space-y-2">
                        {utilityRates.map(rate => (
                          <div
                            key={rate.id}
                            onClick={() => navigate(`/data/rates/${rate.id}`)}
                            className="p-3 bg-gray-50 rounded-md hover:bg-gray-100 cursor-pointer transition-colors"
                          >
                            <p className="text-sm font-medium text-gray-900">{rate.rate_name}</p>
                            <p className="text-xs text-gray-600">{rate.sector}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600">No rates found for this utility</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 overflow-auto p-6">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <LoadingSpinner message="Loading territories..." />
                </div>
              ) : error ? (
                <div className="flex items-center justify-center h-full">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center max-w-md">
                    <p className="text-red-600 font-medium">{error}</p>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-md">
                  <DataTable
                    columns={tableColumns}
                    data={getFilteredTerritories()}
                    onRowClick={(row) => setSelectedTerritory(row)}
                  />
                </div>
              )}
            </div>
          )}
    </div>
  );
};

export default TerritoryMapView;
